import type React from "react";
import { useEffect, useState } from "react";

import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Autocomplete, Box, Chip, IconButton, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetProfessionals } from "api/hooks/useGetProfessionals";
import type { Personnel } from "contexts/CalendarContext";
import { useCalendarContext } from "contexts/CalendarContext";
import { useProfileContext } from "contexts/ProfileContext";
import { MUIBorder, MUIInput, MUITextInputLabel } from "shared/atoms/inputs/StyledInputs";

import { HCPROF_COLORS } from "./Calendar/helpers/transformAppointments";

const CHIP_LIMIT = 3;

export const PersonnelAutocomplete: React.FC = () => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const { selectedPersonnel, setSelectedPersonnel } = useCalendarContext();
  const [professionalOptions, setProfessionalOptions] = useState<Personnel[]>([]);

  invariant(profile);

  const { data = [] } = useGetProfessionals(profile.id);

  useEffect(() => {
    if (data.length > 0) {
      const professionals: Personnel[] = data?.map((professional, i) => ({
        id: professional.health_care_professional_id,
        full_name: professional.health_care_professional_full_name,
        bgColor: HCPROF_COLORS[i]?.bgColor,
        borderColor: HCPROF_COLORS[i]?.borderColor,
        availabilityColor: HCPROF_COLORS[i]?.availabilityColor,
      }));
      setProfessionalOptions(professionals);

      if (selectedPersonnel.length <= 0) {
        setSelectedPersonnel(professionals);
      }
    }
  }, [data]);

  const onChange = (_event: React.ChangeEvent<unknown>, value: Personnel[]) => {
    setSelectedPersonnel(value);
  };

  return (
    <Container>
      <Box display="flex" justifyContent="flex-start" alignItems="center" mb={1} gap={2} flexWrap="wrap">
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={professionalOptions}
          value={selectedPersonnel}
          onChange={onChange}
          getOptionLabel={option => option.full_name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={params => (
            <TextField
              {...params}
              label={
                selectedPersonnel.length === 0
                  ? t("booking.calendar.selected_personnel_all")
                  : t("booking.calendar.selected_personnel", { number: selectedPersonnel.length })
              }
            />
          )}
          renderTags={() => null}
          style={{ width: "320px", background: "#fff", borderRadius: "8px" }}
        />

        <Chips>
          {selectedPersonnel.slice(0, CHIP_LIMIT).map(personnel => {
            return (
              <Chip
                key={personnel.id}
                label={personnel.full_name}
                style={{ background: personnel.bgColor }}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onDelete={() => {}}
                deleteIcon={
                  <IconButton
                    onMouseUp={() => {
                      setSelectedPersonnel(prevState => [
                        ...prevState.filter(prevPersonnel => prevPersonnel.id !== personnel.id),
                      ]);
                    }}
                    size="small"
                  >
                    <FontAwesomeIcon icon={faTimes} size="xs" />
                  </IconButton>
                }
              />
            );
          })}

          {selectedPersonnel.length > CHIP_LIMIT && (
            <MoreCount>
              <Typography variant="subtitle1">+{selectedPersonnel.length - CHIP_LIMIT}</Typography>
            </MoreCount>
          )}
        </Chips>
      </Box>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 32px;

  .MuiAutocomplete-root {
    background-color: white;
    border-radius: ${props => props.theme.borderRadius.basic};
    min-width: 116px;

    .MuiFormLabel-root {
      display: block;
    }
    .MuiFormControl-root {
      .MuiInputBase-root {
        padding: 23px 12px 7px;
        .MuiInputBase-input {
          padding: 0;
        }
      }
    }

    .MuiInputBase-root {
      padding: 23px 12px 7px;

      &.Mui-disabled {
        background-color: ${props => props.theme.colors.greys.light2};
      }
      .MuiChip-root {
        opacity: 1;
        color: ${props => props.theme.colors.primary.base};
      }
    }
    ${MUIInput}
    ${MUIBorder}
    ${MUITextInputLabel}
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .MuiChip-root {
    padding: 4px 8px;
    height: 40px;
    border-radius: 50px;
  }
`;

const MoreCount = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  background-color: ${props => props.theme.colors.redesign.b10};
  border-radius: 50px;
`;
